import classNames from 'classnames';
import lottie, { AnimationItem } from 'lottie-web/build/player/lottie_light';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';

type Props = {
  data: object;
  threshold?: number;
  className?: string;
  loop?: boolean;
};

export const LottiePlay = ({ data, threshold = 0.1, className, loop = false }: Props) => {
  const lottieRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const inViewRef = useRef(false);

  const { ref: inViewObserverRef, inView } = useInView({
    triggerOnce: false,
    threshold,
  });

  // Mirror inView into a ref so the load effect can read the current value
  useEffect(() => {
    inViewRef.current = inView;
    if (animationRef.current) {
      inView ? animationRef.current.play() : animationRef.current.stop();
    }
  }, [inView]);

  useEffect(() => {
    if (!lottieRef.current) return;

    const animInstance = lottie.loadAnimation({
      container: lottieRef.current,
      animationData: data,
      renderer: 'svg',
      loop,
      autoplay: false,
    });

    animationRef.current = animInstance;
    // Apply current inView state immediately (element may already be visible on mount)
    if (inViewRef.current) animInstance.play();

    return () => {
      animInstance.destroy();
      animationRef.current = null;
    };
  }, [data, loop]);

  return (
    <div
      ref={inViewObserverRef}
      className={classNames(className, 'transition-opacity', {
        'opacity-100': inView,
        'opacity-0': !inView,
      })}
    >
      <div ref={lottieRef} className="h-full w-full" />
    </div>
  );
};
